import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const threeConclusions = [
  {
    num: '01',
    title: '评价复杂',
    desc: '复杂在它既讲知识，又讲理论、价值和育人效果。评价的不是一个点，而是一组交织在一起的教学行为。',
    icon: 'ri-puzzle-line',
  },
  {
    num: '02',
    title: '回溯与验证',
    desc: '在大规模、常态化场景中，会遇到证据回溯、尺度统一和效率的问题。引入AI不是否定传统，而是把经验保留、放大、验证。',
    icon: 'ri-shield-star-line',
  },
  {
    num: '03',
    title: '长视频理解',
    desc: '它必须建立在统一指标、专家样本、课堂切片、多模态识别和人机验证之上，才能稳定、准确、可验证地评课。',
    icon: 'ri-brain-line',
  },
];

export default function P23WrapUp({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/8 blur-[150px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            第二部分收束
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI评课的<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">难点</span>
          </h2>
        </div>

        {/* Big statement - 左右分栏 */}
        <div className={`max-w-5xl mx-auto mb-10 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
            {/* 左侧引用文字 */}
            <div className="relative p-6 md:p-8 rounded-2xl glass-liquid border-primary-200/30">
              <i className="ri-double-quotes-l text-3xl text-primary-300/30 mb-3 block" />
              <p className="font-heading text-xl md:text-2xl lg:text-[1.65rem] font-bold text-foreground-950 leading-relaxed">
                难点不在<br />
                <span className="text-foreground-950">"能不能写点评"</span><br />
                而在<br />
                <span className="text-primary-600">"能不能稳定、准确、可验证地评课"</span>
              </p>
              <i className="ri-double-quotes-r text-3xl text-primary-300/30 mt-3 block text-right" />
            </div>

            {/* 右侧配图 */}
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20digital%20evaluation%20concept%20with%20stable%20data%20streams%20accuracy%20metrics%20and%20verification%20checkmarks%2C%20modern%20isometric%20illustration%20style%2C%20soft%20blue%20and%20warm%20gold%20tones%2C%20clean%20minimal%20design%2C%20educational%20technology%20concept%20art%2C%20white%20background%2C%20professional%20quality&width=800&height=600&seq=p23-quote-img-01&orientation=landscape"
                alt="AI评课难点"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Three conclusions */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {threeConclusions.map((conclusion, i) => (
            <div
              key={conclusion.num}
              className="group relative p-6 rounded-2xl glass border-white/60 hover:border-primary-200/50 transition-all duration-500 hover:scale-[1.02]"
              style={{ transitionDelay: `${500 + i * 120}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl glass-blue border-primary-200/40 flex items-center justify-center flex-shrink-0">
                  <i className={`${conclusion.icon} text-lg text-primary-600`} />
                </div>
                <div>
                  <span className="number-font text-xl text-primary-400">{conclusion.num}</span>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mt-1 mb-2">{conclusion.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-950 font-body leading-relaxed">{conclusion.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-8 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-arrow-right-line text-accent-500" />
            <span className="text-sm font-body text-foreground-950">
              下一部分，进入技术核心：<strong className="text-primary-700">AI到底怎样把一节长视频变成一个可评分、可解释、可汇总的评价结果</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}